import { isSameDay, parseISO } from 'date-fns';

import { partOfDayToDayPartNumber } from '../helpers/day-part-number.helper';
import { WorkingDayResponse } from '../types/dto/time-off-working-days.dto';
import { DayPortion } from '../types/time-off-request';
import { TimeOffRequestDetails } from '../types/time-off-request-details';
import { TimeOffRequestFormValues } from './time-off-request-form.schema';

/** Maps a single booked day from the details response into the form-day shape. */
const mapBookedDay = (
  day: TimeOffRequestDetails['days'][number]
): TimeOffRequestFormValues['days'][number] => {
  const partNumber =
    day.portion === DayPortion.Hourly ? undefined : partOfDayToDayPartNumber(day.partOfDay);
  return {
    date: day.date,
    isWorkingDay: true,
    portion: day.portion,
    dayPartNumber: partNumber != null ? String(partNumber) : '',
    minutes: day.portion === DayPortion.Hourly ? (day.minutes ?? 0) : undefined,
  };
};

/**
 * Hydrates the shared {@link TimeOffRequestFormValues} from a fetched request
 * (read + approve modes). The fields are rendered disabled, so no validation
 * runs here — this only maps the details into the unified form shape.
 *
 * `details.days` only carries the booked (working) days, so weekends/holidays in
 * the range are absent. Initial hydration uses just those booked days; once the
 * working-days endpoint resolves, {@link buildDaysFromDetailsAndWorkingDays}
 * re-seeds the list with the full range so non-working days show as disabled
 * rows — exactly like create mode.
 */
export const buildDefaultsFromDetails = (
  details: TimeOffRequestDetails
): TimeOffRequestFormValues => ({
  timeOffTypeId: String(details.type.id),
  dateRange: { from: parseISO(details.startDate), to: parseISO(details.endDate) },
  description: details.description ?? '',
  // Read/approve never validate; kept truthy so the type stays satisfied.
  approvalRequired: true,
  approvers: details.approvers.map(approver => ({
    id: approver.user.id,
    name: approver.user.name,
    profileImageUrl: approver.user.profileImageUrl ?? null,
  })),
  days: details.days.map(mapBookedDay),
});

/**
 * Merges the request's booked days with the full working-days range so the
 * read/approve day list mirrors create: every calendar day in the range is
 * shown, booked working days keep their portion, and non-working days render as
 * disabled "non-working day" rows. Booked days are matched to the range by
 * calendar date (both are start-of-day UTC).
 */
export const buildDaysFromDetailsAndWorkingDays = (
  details: TimeOffRequestDetails,
  workingDays: WorkingDayResponse[]
): TimeOffRequestFormValues['days'] =>
  workingDays.map(workingDay => {
    const booked = details.days.find(day =>
      isSameDay(parseISO(day.date), parseISO(workingDay.date))
    );
    if (booked) return mapBookedDay(booked);
    return {
      date: workingDay.date,
      isWorkingDay: workingDay.isWorkingDay,
      portion: DayPortion.Full,
      dayPartNumber: '',
      minutes: undefined,
    };
  });
